import React, { useEffect, useRef, useState } from 'react';
import { shouldRequestAd } from './yandexRtbGate';

// Статический счётчик для детерминированных ID (без Math.random)
let instanceCounter = 0;

// blockId, уже запрошенные на странице. РСЯ считает запрос по blockId:
// второй render того же blockId = холостой hit без показа (showrate падает).
const liveBlockIds = new Set<string>();

function pushRender(opts: Record<string, unknown>) {
    const w = window as any;
    if (!w.yaContextCb) return;
    w.yaContextCb.push(() => {
        w.Ya?.Context?.AdvManager?.render(opts);
    });
}

// Слот отпускает blockId только если сам его занял (иначе unmount
// соседа сносил destroy'ем все слоты этого blockId).
function releaseBlockId(blockId: string | undefined, didRender: { current: boolean }) {
    if (!didRender.current || !blockId) return;
    didRender.current = false;
    liveBlockIds.delete(blockId);
    try {
        (window as any).Ya?.Context?.AdvManager?.destroy?.({ blockId });
    } catch (e) {
        console.error('Yandex RTB cleanup error:', e);
    }
}

interface YandexRTBProps {
    idMobile?: string;
    idDesktop?: string;
    type?: 'feed' | 'floorAd' | 'adaptive' | 'native';
    style?: React.CSSProperties;
    className?: string;
}

export const YandexRTB: React.FC<YandexRTBProps> = ({
    idMobile,
    idDesktop,
    type = 'feed',
    style,
    className = ''
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const didRenderRef = useRef(false);
    const blockIdRef = useRef<string | undefined>(undefined);
    // Детерминированный ID без Math.random (избегает SSR/hydration mismatch)
    const [containerId] = useState(() => `yandex_rtb_${++instanceCounter}_${idMobile || idDesktop || 'auto'}`);

    useEffect(() => {
        const isDesktop = window.innerWidth >= 1024;
        const blockId = isDesktop ? idDesktop : idMobile;
        if (!blockId) return;

        blockIdRef.current = blockId;
        const platform = isDesktop ? 'desktop' : 'touch';

        if (type === 'floorAd') {
            // FloorAd не требует контейнера и всегда во вьюпорте
            if (!shouldRequestAd({ visible: true, liveBlockIds, blockId })) return;
            liveBlockIds.add(blockId);
            didRenderRef.current = true;
            pushRender({ blockId, type: 'floorAd', platform });
            return () => releaseBlockId(blockIdRef.current, didRenderRef);
        }

        const el = containerRef.current;
        if (!el) return;

        // Запрос рядом с viewport: креатив успевает загрузиться до 2-секундного показа.
        const io = new IntersectionObserver(([entry]) => {
            const visible = entry.isIntersecting && entry.intersectionRatio >= 0.5;
            if (!shouldRequestAd({ visible, liveBlockIds, blockId })) return;
            liveBlockIds.add(blockId);
            didRenderRef.current = true;
            pushRender({
                blockId,
                ...(type === 'native' || type === 'feed' ? { type } : {}),
                renderTo: containerId,
                platform
            });
            io.disconnect();
        }, { threshold: 0.5, rootMargin: '120px 0px' });
        io.observe(el);

        return () => {
            io.disconnect();
            releaseBlockId(blockIdRef.current, didRenderRef);
        };
    }, [idMobile, idDesktop, type, containerId]);

    // Floor ads are injected into the body directly by the Yandex script
    if (type === 'floorAd') {
        return null;
    }

    // Без maxHeight/overflow: клип контейнера резал креатив,
    // РСЯ не засчитывала видимый показ (visibility 16% против 67% у FloorAd).
    return (
        <div
            id={containerId}
            ref={containerRef}
            className={`yandex-rtb-container ${className || ''}`}
            style={{
                minHeight: '80px',
                width: '100%',
                ...style
            }}
        />
    );
};
